<template>
    <div class="flex justify-between items-center gap-2 mb-4">
        <div class="flex items-center gap-2">
            <label class="font-medium">Last Refreshed:</label>
            <label class="font-medium">{{ getHRMSTime(lastRefresh) }}</label>
        </div>
        <div class="flex items-center gap-2">
            <label class="font-medium">Auto Refresh:</label>
            <SelectButton v-model="refreshInterval" :options="refreshOptions" optionLabel="label" optionValue="value"
                 class="p-buttonset-sm" />
        </div>
    </div>  
    <DataTable :value="postbackLog" scrollable :scrollHeight="'90vh'" class="w-full" :sortField="'approvedAt'"
        :sortOrder="-1" :loading="postbackLogLoading" :rowClass="rowData => isRecent(rowData.createdAt) ? 'highlight-row' : ''">
        <Column header="Date Sent" field="createdAt" :headerStyle="headerStyles">
            <template #body="slotProps">    
                <span>{{ getFormattedDate(slotProps.data.createdAt) }}</span>
            </template>
        </Column>
        <Column header="Event" field="eventType" :headerStyle="headerStyles">
            <template #body="slotProps">
                {{ 
                    slotProps.data.eventType === 'cpa' ? 'Qualification' :
                    slotProps.data.eventType === 'ftd' ? 'FTD' :
                    slotProps.data.eventType === 'reg' ? 'Register' :
                    slotProps.data.eventType === 'dep' ? 'Deposit' :
                    slotProps.data.eventType 
                }}
            </template>
        </Column>
        <Column header="Advertiser"  :headerStyle="headerStyles">
            <template #body="{data}">
                <div>{{ data.advertiserId?.name || data.partner || 'N/A' }}<span v-if="data.market" :class="`fi fi-${data.market.toLowerCase()}`" class="ml-2"></span></div>
            </template>
        </Column>
        <Column header="Reuquested url"  :headerStyle="headerStyles">
            <template #body="slotProps">
                <div>{{ slotProps.data.fullUrl }}</div>
            </template>
        </Column>
        <Column header="Status" field="statusCode" :headerStyle="headerStyles">
            <template #body="slotProps">
                <Tag 
                    :value="slotProps.data.statusCode"
                    :severity="slotProps.data.statusCode === 200 ? 'success' : 'danger'"
                    class="dark:bg-opacity-20"
                />
            </template>
        </Column>
    </DataTable>
    <AutoRefreshPausedDialog :show="autoRefreshPaused" />
</template>

<script setup>

import { shallowRef, onMounted, computed, ref, watch, onUnmounted } from 'vue';
import PostbackService from '@/services/PostbackService';
import { useStore } from 'vuex';
import { headerStyles } from '@/global/headerStyles';
import { getFormattedDate, getHRMSTime } from '@/utils/dateHelper';
import AutoRefreshPausedDialog from '../AutoRefreshPausedDialog.vue';
const store = useStore();
const userId = computed(() => store.getters.userId);
const lastRefresh = ref(null);
const postbackLog = shallowRef([]);
const postbackLogLoading = ref(false);
const refreshInterval = ref(300000);
const refreshOptions = ref([
    { label: '1 minute', value: 60000 },
    { label: '5 minutes', value: 300000 },
]);
const timerId = ref(null);
const recentThreshold = computed(() => Date.now() - refreshInterval.value);
const autoRefreshPaused = ref(false);
let autoRefreshTimeout = null;

const props = defineProps({
    dateRange: { type: Array, default: null }
});

const dateRange = computed(() => props.dateRange);

const getPostbackLog = async (from, to) => {
    postbackLogLoading.value = true;
    try {
        const response = await PostbackService.getPostbackLog(userId.value, {from, to});
        postbackLog.value = response.data;
        lastRefresh.value = Date.now();
    } catch (error) {
        console.error(error);
    }
    postbackLogLoading.value = false;
}

const startTimer = () => {
    if (timerId.value) clearInterval(timerId.value);
    timerId.value = setInterval(() => {
        if (!dateRange.value || dateRange.value.length < 2 || !dateRange.value[1]) return;
        getPostbackLog(dateRange.value[0], dateRange.value[1]);
    }, refreshInterval.value);
};

function startAutoRefreshTimeout() {
    if (autoRefreshTimeout) clearTimeout(autoRefreshTimeout);
    autoRefreshTimeout = setTimeout(() => {
        autoRefreshPaused.value = true;
        if (timerId.value) clearInterval(timerId.value);
    }, 7200000); // 2 hours
}

watch(refreshInterval, () => {
    startAutoRefreshTimeout();
    startTimer();
});

watch(dateRange, () => {
    if (dateRange.value && dateRange.value.length >= 2 && dateRange.value[1]) {
        getPostbackLog(dateRange.value[0], dateRange.value[1]);
    }
}, { deep: true });

onMounted(() => {
    startAutoRefreshTimeout();
    startTimer();

    if (dateRange.value && dateRange.value.length >= 2 && dateRange.value[1]) {
        getPostbackLog(dateRange.value[0], dateRange.value[1]);
    }
});

onUnmounted(() => {
    if (timerId.value) clearInterval(timerId.value);
    if (autoRefreshTimeout) clearTimeout(autoRefreshTimeout);
});

const isRecent = (createdAt) => {
    return new Date(createdAt).getTime() >= recentThreshold.value;
};

</script>

<style scoped>
:deep(.highlight-row) {
    animation: highlight-flash 0.8s steps(2, jump-none) infinite;
    color: #fff !important; /* ensure text is white */
}

@keyframes highlight-flash {
    0% {
        background-color: rgba(34,197,94,0.25); /* subtle green */
    }
    50% {
        background-color: transparent;
    }
    100% {
        background-color: rgba(34,197,94,0.25);
    }
}
</style>